import { type FC } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { IntegrationServiceId } from '../../../../../common/integrations'
import {
  type AccountSchedulingSettings,
  AvailabilityMode,
  type AvailabilitySchedule,
  type Location,
  MeetingMode,
  type SchedulingEventType,
} from '../../../../../models/Scheduling'
import { useUserTimezone } from '../../../../../common/useUserTimezone'
import { DefaultAvailabilityEditor } from '../../../../Scheduling/shared/DefaultAvailabilityEditor'
import { buildEventTypeLocationOptions, eventTypeLocationPatch, eventTypeLocationSelectValue } from '../../../../Scheduling/shared/eventTypeOptions'
import { useDefaultLocationPreviews } from '../../../../Scheduling/shared/useDefaultLocationPreviews'
import { formatTimezoneLabel } from '../../../../Scheduling/shared/schedulingDisplay'
import { resolveLocationPreviewId } from '../../../../Scheduling/flows/form/scheduleHelpers'

const BUFFER_OPTIONS = [0, 5, 10, 15, 30]
const NOTICE_OPTIONS = [0, 4, 12, 24, 48, 72]
const WINDOW_OPTIONS = [14, 30, 60, 90, 120]

const bufferLabel = (minutes: number): string => (minutes === 0 ? 'None' : `${minutes} minutes`)
const noticeLabel = (hours: number): string => (hours === 0 ? 'None' : `${hours} hours`)
const windowLabel = (days: number): string => `${days} days`

const VIDEO_PLATFORMS = [IntegrationServiceId.ZOOM, IntegrationServiceId.TEAMS, IntegrationServiceId.MEET]

interface AvailabilityTabProps {
  draft: SchedulingEventType
  accountSettings?: AccountSchedulingSettings
  locations: Location[]
  disabled: boolean
  onDraftChange: (patch: Partial<SchedulingEventType>) => void
}

interface SectionHeaderProps {
  children: string
}

const SectionHeader: FC<SectionHeaderProps> = ({ children }) => <div className='lead-small zocks-text-gray-500 text-uppercase'>{children}</div>

interface FieldLabelProps {
  label: string
  helper: string
}

const FieldLabel: FC<FieldLabelProps> = ({ label, helper }) => (
  <>
    <div className='bold'>{label}</div>
    <div className='paragraph-small zocks-text-gray-600'>{helper}</div>
  </>
)

export const AvailabilityTab: FC<AvailabilityTabProps> = ({ draft, accountSettings, locations, disabled, onDraftChange }) => {
  const isInPerson = draft.meetingMode === MeetingMode.IN_PERSON
  const accountTimezone = useUserTimezone()

  const { accountDefaultLocation, userDefaultLocation } = useDefaultLocationPreviews(locations)
  const locationOptions = buildEventTypeLocationOptions(locations, accountDefaultLocation, userDefaultLocation, draft)
  const resolvedLocationId = resolveLocationPreviewId(draft.location, accountDefaultLocation?.id, userDefaultLocation?.id)
  const resolvedLocation: Location | undefined = resolvedLocationId
    ? [...locations, ...(userDefaultLocation ? [userDefaultLocation] : [])].find((location) => location.id === resolvedLocationId)
    : undefined

  const customSchedule = draft.availabilityMode === AvailabilityMode.CUSTOM ? (draft.availabilitySchedule ?? null) : null
  const accountDefaultAvailability: AvailabilitySchedule = accountSettings?.defaults?.availabilitySchedule ?? { days: [] }

  return (
    <div className='d-flex flex-column gap-4'>
      <div className='d-flex flex-column gap-3'>
        <SectionHeader>Where</SectionHeader>
        <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <FieldLabel label='Location' helper={`This event type is set as ${isInPerson ? 'an in-person' : 'a virtual'} event. Change it in the sidebar.`} />
          </div>
          {isInPerson && locations.length > 0 ? (
            <Form.Select
              className='w-auto'
              aria-label='Location'
              value={eventTypeLocationSelectValue(draft)}
              disabled={disabled}
              onChange={(event) => onDraftChange(eventTypeLocationPatch(event.currentTarget.value))}
            >
              <option value='' disabled>
                Select a location
              </option>
              {locationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              <option value='custom' disabled>
                Custom location (coming soon)
              </option>
            </Form.Select>
          ) : null}
        </div>
        {isInPerson && locations.length === 0 ? (
          <div className='d-flex align-items-start zocks-gap-0.5 p-3 rounded-2 zocks-bg-color-warning-background paragraph-small'>
            <i className='bi bi-exclamation-triangle zocks-text-gray-600 mt-1' />
            <span>Add a location under Availability before you can save this in-person event type.</span>
          </div>
        ) : isInPerson ? (
          <div className='d-flex flex-column gap-2 p-3 rounded-2 zocks-bg-color-gray-100'>
            <span className='d-flex align-items-center zocks-gap-0.5 paragraph-small'>
              <i className='bi bi-geo-alt zocks-text-gray-600' />
              {resolvedLocation?.address ?? 'No location set'}
            </span>
            {resolvedLocation ? (
              <span className='d-flex align-items-center zocks-gap-0.5 paragraph-small'>
                <i className='bi bi-building zocks-text-gray-600' />
                {resolvedLocation.name}
              </span>
            ) : null}
            <span className='d-flex align-items-center zocks-gap-0.5 paragraph-small'>
              <i className='bi bi-globe zocks-text-gray-600' />
              {formatTimezoneLabel(accountTimezone)}
            </span>
          </div>
        ) : (
          <div className='d-flex align-items-start justify-content-between gap-3 p-3 rounded-2 zocks-bg-color-gray-100'>
            <div className='d-flex align-items-start zocks-gap-0.5'>
              <i className='bi bi-info-circle zocks-text-gray-600 mt-1' />
              <div className='d-flex flex-column'>
                <span className='paragraph-small'>Each user sets their own video platform. Zocks adds the call link when a meeting is scheduled.</span>
                <span className='paragraph-small zocks-text-gray-600'>Works with Zoom, Teams and Google Meet.</span>
                <NavLink to='/account/integrations' className='paragraph-small'>
                  Learn more
                </NavLink>
              </div>
            </div>
            <div className='d-flex align-items-center zocks-gap-0.5 flex-shrink-0'>
              {VIDEO_PLATFORMS.map((serviceId) => (
                <IntegrationIcon key={serviceId} serviceId={serviceId} width='1.25rem' height='1.25rem' />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className='d-flex flex-column gap-3'>
        <SectionHeader>When</SectionHeader>
        <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <FieldLabel label='Timezone' helper='Working hours are interpreted in this timezone.' />
            <span className='paragraph-small zocks-text-gray-600'>
              Managed by your account default timezone in your <NavLink to='/account/profile-settings'>account settings</NavLink>.
            </span>
          </div>
          <Form.Control className='w-auto' value={formatTimezoneLabel(accountTimezone)} disabled readOnly aria-label='Timezone' />
        </div>

        <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <FieldLabel label='Weekly hours' helper='Set the days and times clients can book this event type.' />
          </div>
          <Form.Select
            className='w-auto'
            aria-label='Weekly hours mode'
            value={customSchedule ? 'custom' : 'account'}
            disabled={disabled}
            onChange={(event) =>
              onDraftChange(
                event.currentTarget.value === 'custom'
                  ? { availabilityMode: AvailabilityMode.CUSTOM, availabilitySchedule: accountDefaultAvailability }
                  : { availabilityMode: AvailabilityMode.ACCOUNT_DEFAULT, availabilitySchedule: undefined },
              )
            }
          >
            <option value='account'>Account default</option>
            <option value='custom'>Custom availability</option>
          </Form.Select>
        </div>
        <div className='p-3 rounded-2 zocks-bg-color-gray-100'>
          <DefaultAvailabilityEditor
            availability={customSchedule ?? accountDefaultAvailability}
            readOnly={disabled || !customSchedule}
            onAvailabilityChange={(availabilitySchedule) => onDraftChange({ availabilitySchedule })}
          />
        </div>
      </div>

      <Row className='gy-3'>
        <Col md={6}>
          <FieldLabel label='Buffer before' helper='Time blocked before each booking.' />
          <Form.Select
            value={draft.bufferBeforeMinutes ?? 0}
            disabled={disabled}
            aria-label='Buffer before'
            onChange={(event) => onDraftChange({ bufferBeforeMinutes: Number(event.currentTarget.value) })}
          >
            {BUFFER_OPTIONS.map((minutes) => (
              <option key={minutes} value={minutes}>
                {bufferLabel(minutes)}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={6}>
          <FieldLabel label='Buffer after' helper='Time blocked after each booking.' />
          <Form.Select
            value={draft.bufferAfterMinutes ?? 0}
            disabled={disabled}
            aria-label='Buffer after'
            onChange={(event) => onDraftChange({ bufferAfterMinutes: Number(event.currentTarget.value) })}
          >
            {BUFFER_OPTIONS.map((minutes) => (
              <option key={minutes} value={minutes}>
                {bufferLabel(minutes)}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={6}>
          <FieldLabel label='Minimum advance notice' helper='How far ahead clients must book.' />
          <Form.Select
            value={(draft.minimumNoticeMinutes ?? 0) / 60}
            disabled={disabled}
            aria-label='Minimum advance notice'
            onChange={(event) => onDraftChange({ minimumNoticeMinutes: Number(event.currentTarget.value) * 60 })}
          >
            {NOTICE_OPTIONS.map((hours) => (
              <option key={hours} value={hours}>
                {noticeLabel(hours)}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={6}>
          <FieldLabel label='Booking window' helper='How far in the future clients can book.' />
          <Form.Select
            value={draft.bookingWindowDays ?? 0}
            disabled={disabled}
            aria-label='Booking window'
            onChange={(event) => onDraftChange({ bookingWindowDays: Number(event.currentTarget.value) })}
          >
            {WINDOW_OPTIONS.map((days) => (
              <option key={days} value={days}>
                {windowLabel(days)}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>
    </div>
  )
}
